/**
 * "등록" 버튼을 눌렀을 때 어떤 API 로 보낼지, 그 전에 확인을 받을지 정한다.
 *
 * 화면에서 조건식으로 흩어져 있던 판정을 한곳에 모은 이유는 **1:1 모드인데 1:2 API 가
 * 나가던 버그** 때문이다. 원래 조건은 이렇게 생겼었다.
 *
 * ```
 * else if (
 *     (!isOneOnOneClass && first?.canMakeScheduleSession === 0) ||
 *     second?.canMakeScheduleSession === 0   // ← 이 줄에 !isOneOnOneClass 가 없다
 * ) { ... submitOneOnTwoSchedule() }
 * ```
 *
 * `||` 의 **두 번째 항에만 모드 가드가 빠져 있어서**, 1:1 모드여도 두 번째 회원의 잔여가
 * 0이면 이 분기로 들어가 1:2 등록이 나갔다. 두 번째 회원은 1:1 화면에 그려지지도 않는데
 * (선택 카드는 `selectedMember[0]` 만 그린다) 일정은 2건 만들어진다.
 *
 * 공유 수강권과 무관한 **원래부터 있던 버그**다. 다만 공유를 목록에 띄우면 이 경로가
 * 1:2 엔드포인트(duplicable)로 공유 수강권을 공유 문맥 없이 보내게 되어 400 까지 더해진다.
 *
 * 모드를 **가장 바깥에서 한 번만** 가르는 형태로 바꿔, 같은 실수가 다시 나올 자리를 없앴다.
 * 1:1 모드에서는 어떤 입력이 와도 `ONE_ON_TWO` 가 나오지 않는다.
 */
export type ScheduleSubmitTarget = 'ONE_ON_ONE' | 'ONE_ON_TWO';

export type ScheduleSubmitPlan =
    /** 공유 풀은 잔여를 초과해 등록할 수 없다. 확인 없이 막는다. */
    | { kind: 'SHARED_EXHAUSTED_BLOCKED' }
    /** 개인 수강권은 다 써도 "그래도 등록할지" 물어본 뒤 진행한다. */
    | { kind: 'CONFIRM_EXHAUSTED'; submit: ScheduleSubmitTarget }
    | { kind: 'SUBMIT'; submit: ScheduleSubmitTarget };

type MembershipLike = {
    canMakeScheduleSession?: number;
    isSharedMembership?: boolean;
    [key: string]: unknown;
} | null | undefined;

/** 화면이 쓰는 판정과 정확히 같은 비교. `undefined`(수강권 없음)는 소진이 아니다. */
const isExhausted = (membership: MembershipLike) =>
    membership?.canMakeScheduleSession === 0;

export const resolveScheduleSubmitPlan = ({
    isOneOnOneClass,
    oneOnOneMembership,
    firstMembership,
    secondMembership,
}: {
    isOneOnOneClass: boolean;
    /** 1:1 모드에서 고른 수강권. */
    oneOnOneMembership?: MembershipLike;
    /** 1:2 모드 첫 번째 회원의 수강권. */
    firstMembership?: MembershipLike;
    /** 1:2 모드 두 번째 회원의 수강권. */
    secondMembership?: MembershipLike;
}): ScheduleSubmitPlan => {
    if (isOneOnOneClass) {
        // 1:1 모드는 두 번째 회원을 아예 보지 않는다. 화면에 안 그리는 값으로
        // 제출 방식이 바뀌면 직원이 만든 적 없는 일정이 생긴다.
        if (oneOnOneMembership?.isSharedMembership === true && isExhausted(oneOnOneMembership)) {
            return { kind: 'SHARED_EXHAUSTED_BLOCKED' };
        }
        if (isExhausted(oneOnOneMembership)) {
            return { kind: 'CONFIRM_EXHAUSTED', submit: 'ONE_ON_ONE' };
        }
        return { kind: 'SUBMIT', submit: 'ONE_ON_ONE' };
    }

    if (isExhausted(firstMembership) || isExhausted(secondMembership)) {
        return { kind: 'CONFIRM_EXHAUSTED', submit: 'ONE_ON_TWO' };
    }
    return { kind: 'SUBMIT', submit: 'ONE_ON_TWO' };
};

/**
 * 제출 계획 위에 날짜 경고를 얹어 **확인 창을 한 번만** 띄우도록 합친다.
 *
 * - `SHARED_EXHAUSTED_BLOCKED` 는 그대로 막는다. 날짜 경고가 있어도 창을 띄우지 않는다 —
 *   "그래도 잡기"로 공유 소진 차단을 우회하는 길이 생기면 안 된다.
 * - 1:1 / 1:2 대상(`submit`)은 제출 계획이 정한 값을 그대로 옮긴다. 날짜 경고는 대상을 바꾸지 않는다.
 * - "다 쓴 수강권" 확인과 날짜 경고가 겹치면 한 창에 함께 싣는다(창 두 번 금지).
 */
export type ScheduleSubmitDecision<Warning = unknown> =
    | { kind: 'BLOCKED_SHARED_EXHAUSTED' }
    /**
     * 공유 수강권이 수업 시각에 기간 밖이다. 서버가 항상 409 로 거절하므로 전송하지 않는다.
     * 1:2·개인과 섞인 요청도 공유가 하나라도 걸리면 전체를 막는다(일부만 보내면 부분 성공이 남는다).
     */
    | { kind: 'BLOCKED_SHARED_DATE'; sharedDateWarnings: Warning[] }
    | {
          kind: 'CONFIRM';
          submit: ScheduleSubmitTarget;
          exhausted: boolean;
          dateWarnings: Warning[];
      }
    | { kind: 'SUBMIT'; submit: ScheduleSubmitTarget };

export const resolveScheduleSubmitDecision = <Warning extends { isShared?: boolean }>(
    plan: ScheduleSubmitPlan,
    dateWarnings: Warning[],
): ScheduleSubmitDecision<Warning> => {
    // 공유 소진 차단이 먼저다. 둘 다 걸리면 기존 소진 안내를 그대로 낸다.
    if (plan.kind === 'SHARED_EXHAUSTED_BLOCKED') return { kind: 'BLOCKED_SHARED_EXHAUSTED' };
    const sharedDateWarnings = dateWarnings.filter((warning) => warning.isShared === true);
    if (sharedDateWarnings.length > 0) {
        return { kind: 'BLOCKED_SHARED_DATE', sharedDateWarnings };
    }
    const exhausted = plan.kind === 'CONFIRM_EXHAUSTED';
    if (exhausted || dateWarnings.length > 0) {
        return { kind: 'CONFIRM', submit: plan.submit, exhausted, dateWarnings };
    }
    return { kind: 'SUBMIT', submit: plan.submit };
};

export const EXHAUSTED_CONFIRM_TITLE = '다 쓴 수강권이에요!';
const EXHAUSTED_CONFIRM_HEADLINE = '‘등록 가능’ 회차 전부 수업이 등록되어있어요!';
const EXHAUSTED_CONFIRM_QUESTION = '그래도 해당 수강권으로 더 등록하시겠어요?';
export const DATE_WARNING_CONFIRM_TITLE = '수강권 날짜를 확인해 주세요';
export const SHARED_DATE_BLOCK_TITLE = '이 날짜엔 잡을 수 없어요';
/** 화면의 기존 공유 소진 차단 알럿과 같은 글자. */
export const SHARED_EXHAUSTED_BLOCK_TITLE = '등록 가능 회차 부족';
export const SHARED_EXHAUSTED_BLOCK_MESSAGE =
    '공유 수강권의 등록 가능 회차가 남아있지 않습니다\n기존 수업을 취소하거나 공유 수강권을 추가한 뒤 다시 시도해주세요';

export type ScheduleConfirmAlertContent = {
    title: string;
    content: string;
    confirmText: string;
    cancelText: string;
};

/**
 * 확인 창 문구.
 *
 * 날짜 경고가 없으면 **기존 "다 쓴 수강권" 창과 글자 하나 다르지 않게** 낸다.
 * 날짜 경고가 있으면 날짜 창으로 띄우고, 다 쓴 수강권이면 그 사실을 맨 앞 줄에 붙인다.
 */
export const buildScheduleConfirmAlertContent = ({
    exhausted,
    dateWarningMessage,
}: {
    exhausted: boolean;
    /** formatScheduleDateWarningMessage 결과. 경고가 없으면 빈 문자열. */
    dateWarningMessage: string;
}): ScheduleConfirmAlertContent => {
    if (!dateWarningMessage) {
        return {
            title: EXHAUSTED_CONFIRM_TITLE,
            content: `${EXHAUSTED_CONFIRM_HEADLINE}\n${EXHAUSTED_CONFIRM_QUESTION}`,
            confirmText: '등록하기',
            cancelText: '취소',
        };
    }
    return {
        title: DATE_WARNING_CONFIRM_TITLE,
        content: exhausted
            ? `${EXHAUSTED_CONFIRM_HEADLINE}\n\n${dateWarningMessage}`
            : dateWarningMessage,
        confirmText: '그래도 잡기',
        cancelText: '취소',
    };
};
